import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useCreateSystemSetting } from 'src/features/systems/api/systemSettings/useCreateSystemSetting';
import {
    LIST_SYSTEM_SETTING_RESOURCE,
    useListSystemSetting,
} from 'src/features/systems/api/systemSettings/useListSystemSetting';
import { useUpdateSystemSetting } from 'src/features/systems/api/systemSettings/useUpdateSystemSetting';
import { displaySettingsMessages } from 'src/features/systems/utils/displaySettingsMessages';
import { DisplaySetting } from 'src/features/systems/utils/displaySettingTypes';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { Button } from 'src/registry/ui/Button';
import { Form } from 'src/registry/ui/Form';
import { ICON_MAP } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';
import {
    DisplaySettingCreateFormSchemaValues,
    displaySettingFormSchema,
    DisplaySettingFormSchemaValues,
} from 'src/features/systems/utils/displaySettingFormSchema';
import { useParking } from 'src/providers/ParkingProvider';
import { SYSTEM_SETTINGS_CATEGORY, SYSTEM_SETTINGS_CODE } from 'src/features/systems/utils/types';
import DisplaySettingCodeInputs from 'src/features/systems/components/displaySettings/DisplaySettingCodeInputs';

const DisplaySettings = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const createToastShown = React.useRef(false);
    const updateToastShown = React.useRef(false);

    const { data: settingsData } = useListSystemSetting({ parkingId });

    const { mutate: createDisplaySetting, isPending } = useCreateSystemSetting({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SYSTEM_SETTING_RESOURCE] });
            if (!createToastShown.current) {
                createToastShown.current = true;
                toast({
                    title: formatMessage(displaySettingsMessages.displaySettingsCreated),
                    description: formatMessage(displaySettingsMessages.displaySettingsCreatedSuccess),
                });
            }
        },
    });

    const { mutate: updateDisplaySetting, isPending: isUpdatePending } = useUpdateSystemSetting({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SYSTEM_SETTING_RESOURCE] });
            if (!updateToastShown.current) {
                updateToastShown.current = true;
                toast({
                    title: formatMessage(displaySettingsMessages.displaySettingsUpdated),
                    description: formatMessage(displaySettingsMessages.displaySettingsUpdatedSuccess),
                });
            }
        },
    });

    const settingsDataResults = (settingsData?.results as DisplaySetting[]) ?? [];

    const getDataBasedOnSettingCode = (code: Partial<SYSTEM_SETTINGS_CODE>): DisplaySetting | undefined => {
        return settingsDataResults?.find((identifier) => identifier.code === code);
    };

    const successfulEntranceMessageData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.SUCCESSFUL_ENTRANCE_MESSAGE);
    const rejectedEntranceMessageData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.REJECTED_ENTRANCE_MESSAGE);
    const successfulExitMessageData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.SUCCESSFUL_EXIT_MESSAGE);
    const rejectedExitMessageData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.REJECTED_EXIT_MESSAGE);
    const dateMessageData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.DATE_MESSAGE);
    const spacesMessageData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.SPACES_MESSAGE);
    const lprMessageData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.LPR_MESSAGE);
    const ticketMessageData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.TICKET_MESSAGE);
    const cardMessageData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.CARD_MESSAGE);

    const defaultValues = React.useMemo(() => {
        return {
            successful_entrance_message: successfulEntranceMessageData?.value ?? undefined,
            rejected_entrance_message: rejectedEntranceMessageData?.value ?? undefined,
            successful_exit_message: successfulExitMessageData?.value ?? undefined,
            rejected_exit_message: rejectedExitMessageData?.value ?? undefined,
            date_message: dateMessageData?.value ?? undefined,
            spaces_message: spacesMessageData?.value ?? undefined,
            lpr_message: lprMessageData?.value ?? undefined,
            ticket_message: ticketMessageData?.value ?? undefined,
            card_message: cardMessageData?.value ?? undefined,
        };
    }, [
        successfulEntranceMessageData,
        rejectedEntranceMessageData,
        successfulExitMessageData,
        rejectedExitMessageData,
        dateMessageData,
        spacesMessageData,
        lprMessageData,
        ticketMessageData,
        cardMessageData,
    ]);

    const form = useForm<DisplaySettingFormSchemaValues>({
        resolver: zodResolver(displaySettingFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, defaultValues]);

    const onSubmit: SubmitHandler<DisplaySettingFormSchemaValues> = (values) => {
        createToastShown.current = false;
        updateToastShown.current = false;
        const dirtyFields = form.formState.dirtyFields;

        const editedFields: Partial<
            Record<
                SYSTEM_SETTINGS_CODE,
                { settingData: DisplaySetting | undefined; submitData: DisplaySettingCreateFormSchemaValues }
            >
        > = Object.keys(dirtyFields).reduce(
            (acc, key) => {
                const typedKey = key as keyof DisplaySettingFormSchemaValues;
                acc[typedKey] = values[typedKey];
                return acc;
            },
            {} as Record<keyof DisplaySettingFormSchemaValues, any>,
        );

        const successfulEntranceMessageObject = {
            category: SYSTEM_SETTINGS_CATEGORY.GATEWAY_DISPLAY,
            code: SYSTEM_SETTINGS_CODE.SUCCESSFUL_ENTRANCE_MESSAGE,
            value: values['successful_entrance_message'],
        };
        const rejectedEntranceMessageObject = {
            category: SYSTEM_SETTINGS_CATEGORY.GATEWAY_DISPLAY,
            code: SYSTEM_SETTINGS_CODE.REJECTED_ENTRANCE_MESSAGE,
            value: values['rejected_entrance_message'],
        };
        const successfulExitMessageObject = {
            category: SYSTEM_SETTINGS_CATEGORY.GATEWAY_DISPLAY,
            code: SYSTEM_SETTINGS_CODE.SUCCESSFUL_EXIT_MESSAGE,
            value: values['successful_exit_message'],
        };
        const rejectedExitMessageObject = {
            category: SYSTEM_SETTINGS_CATEGORY.GATEWAY_DISPLAY,
            code: SYSTEM_SETTINGS_CODE.REJECTED_EXIT_MESSAGE,
            value: values['rejected_exit_message'],
        };
        const dateMessageObject = {
            category: SYSTEM_SETTINGS_CATEGORY.GATEWAY_DISPLAY,
            code: SYSTEM_SETTINGS_CODE.DATE_MESSAGE,
            value: values['date_message'],
        };
        const spacesMessageObject = {
            category: SYSTEM_SETTINGS_CATEGORY.GATEWAY_DISPLAY,
            code: SYSTEM_SETTINGS_CODE.SPACES_MESSAGE,
            value: values['spaces_message'],
        };
        const lprMessageObject = {
            category: SYSTEM_SETTINGS_CATEGORY.GATEWAY_DISPLAY,
            code: SYSTEM_SETTINGS_CODE.LPR_MESSAGE,
            value: values['lpr_message'],
        };
        const ticketMessageObject = {
            category: SYSTEM_SETTINGS_CATEGORY.GATEWAY_DISPLAY,
            code: SYSTEM_SETTINGS_CODE.TICKET_MESSAGE,
            value: values['ticket_message'],
        };
        const cardMessageObject = {
            category: SYSTEM_SETTINGS_CATEGORY.GATEWAY_DISPLAY,
            code: SYSTEM_SETTINGS_CODE.CARD_MESSAGE,
            value: values['card_message'],
        };

        const displaySettingsCodeFieldsMap: Partial<
            Record<
                SYSTEM_SETTINGS_CODE,
                { settingData: DisplaySetting | undefined; submitData: DisplaySettingCreateFormSchemaValues }
            >
        > = {
            successful_entrance_message: {
                settingData: successfulEntranceMessageData,
                submitData: successfulEntranceMessageObject,
            },
            rejected_entrance_message: {
                settingData: rejectedEntranceMessageData,
                submitData: rejectedEntranceMessageObject,
            },
            successful_exit_message: {
                settingData: successfulExitMessageData,
                submitData: successfulExitMessageObject,
            },
            rejected_exit_message: { settingData: rejectedExitMessageData, submitData: rejectedExitMessageObject },
            date_message: { settingData: dateMessageData, submitData: dateMessageObject },
            spaces_message: { settingData: spacesMessageData, submitData: spacesMessageObject },
            lpr_message: { settingData: lprMessageData, submitData: lprMessageObject },
            ticket_message: { settingData: ticketMessageData, submitData: ticketMessageObject },
            card_message: { settingData: cardMessageData, submitData: cardMessageObject },
        };

        (
            Object.entries(displaySettingsCodeFieldsMap) as [
                keyof typeof displaySettingsCodeFieldsMap,
                { settingData: DisplaySetting; submitData: DisplaySettingCreateFormSchemaValues },
            ][]
        ).forEach(([fieldName, { settingData, submitData }]) => {
            if (editedFields[fieldName]) {
                if (!settingData?.id) {
                    createDisplaySetting({ ...submitData, parkingId });
                }
                if (settingData?.id) {
                    updateDisplaySetting({
                        id: settingData?.id,
                        parkingId,
                        ...submitData,
                    });
                }
            }
        });
    };

    return (
        <div className="mt-6">
            <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
                <DisplaySettingCodeInputs
                    form={form}
                    code="successful_entrance_message"
                    sectionTitle={formatMessage(displaySettingsMessages.successfulEntranceMessage)}
                />
                <DisplaySettingCodeInputs
                    form={form}
                    code="rejected_entrance_message"
                    sectionTitle={formatMessage(displaySettingsMessages.rejectedEntranceMessage)}
                />
                <DisplaySettingCodeInputs
                    form={form}
                    code="successful_exit_message"
                    sectionTitle={formatMessage(displaySettingsMessages.successfulExitMessage)}
                />
                <DisplaySettingCodeInputs
                    form={form}
                    code="rejected_exit_message"
                    sectionTitle={formatMessage(displaySettingsMessages.rejectedExitMessage)}
                />
                <DisplaySettingCodeInputs
                    form={form}
                    code="date_message"
                    sectionTitle={formatMessage(displaySettingsMessages.dateMessage)}
                />
                <DisplaySettingCodeInputs
                    form={form}
                    code="spaces_message"
                    sectionTitle={formatMessage(displaySettingsMessages.spacesMessage)}
                />
                <DisplaySettingCodeInputs
                    form={form}
                    code="lpr_message"
                    sectionTitle={formatMessage(displaySettingsMessages.lprMessage)}
                />
                <DisplaySettingCodeInputs
                    form={form}
                    code="ticket_message"
                    sectionTitle={formatMessage(displaySettingsMessages.ticketMessage)}
                />
                <DisplaySettingCodeInputs
                    form={form}
                    code="card_message"
                    sectionTitle={formatMessage(displaySettingsMessages.cardMessage)}
                />
                <div className="flex justify-end">
                    <Button
                        className="w-full sm:w-auto"
                        type="submit"
                        disabled={isPending || isUpdatePending || !form.formState.isDirty}
                    >
                        {(isPending || isUpdatePending) && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="animate-spin text-lg" />
                        )}
                        {formatMessage(commonMessages.saveChanges)}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default DisplaySettings;
